import React, { useState, Fragment, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog, Transition } from '@headlessui/react';
import { submitQuickOrder } from '../api/orders';
import { Product } from '../api/products';
import { getImageUrl } from '../utils/image';

import { validateMoroccanPhone } from '../utils/phoneValidator';

interface QuickOrderModalProps {
    isOpen: boolean;
    closeModal: () => void;
    product: Product | null;
    variant?: { id: number; name: string; name_ar?: string; price: string; } | null;
    initialQuantity?: number;
}

const QuickOrderModal: React.FC<QuickOrderModalProps> = ({ isOpen, closeModal, product, variant, initialQuantity = 1 }) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [formData, setFormData] = useState({ name: '', phone: '', city: '', address: '' });
    const [qty, setQty] = useState(initialQuantity);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [selectedVariant, setSelectedVariant] = useState<{ id: number; name: string; name_ar?: string; price: string; } | null>(variant || null);

    // Sync quantity when modal opens or initialQuantity changes
    useEffect(() => {
        if (isOpen) {
            setQty(initialQuantity);
        }
    }, [isOpen, initialQuantity]);

    // Update selected variant when prop changes or product changes
    useEffect(() => {
        if (product?.variants && product.variants.length > 0) {
            if (variant && variant.id) {
                setSelectedVariant(variant);
            } else {
                // Check if there's a variant explicitly named "standart"
                const standartVariant = product.variants.find(v => v.name.toLowerCase() === 'standart');
                setSelectedVariant(standartVariant || null);
            }
        } else {
            setSelectedVariant(null);
        }
    }, [product, variant, isOpen]);

    const activeVariant = selectedVariant;

    // Calculate prices
    const unitPrice = activeVariant
        ? parseFloat(activeVariant.price)
        : (product?.discount_price || product?.price || 0);

    const totalPrice = unitPrice * qty;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!validateMoroccanPhone(formData.phone)) {
            setError(t('validation.invalidPhone', 'رقم الهاتف غير صحيح'));
            return;
        }

        if (!product) return;

        const phoneNumber = '212637131815';
        const productName = isRTL ? product.title_ar : product.title_fr;
        const variantName = activeVariant ? (isRTL ? (activeVariant.name_ar || activeVariant.name) : activeVariant.name) : (isRTL ? (product.unit_name_ar || product.unit_name_fr) : product.unit_name_fr);

        const messageLines = [
            `🛍️ *Nouvelle Commande / طلب جديد*`,
            `------------------------`,
            `*Produit / المنتج:* ${productName}`,
            ...(variantName ? [`*Option / الخيار:* ${variantName}`] : []),
            `*Quantité / الكمية:* ${qty}`,
            `*Prix Total / السعر الإجمالي:* ${totalPrice.toFixed(2)} ${product.currency}`,
            `------------------------`,
            `*Nom / الاسم:* ${formData.name}`,
            `*Téléphone / الهاتف:* ${formData.phone}`,
            `*Ville / المدينة:* ${formData.city}`,
            `*Adresse / العنوان:* ${formData.address}`
        ];

        const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(messageLines.join('\n'))}`;
        window.open(whatsappUrl, '_blank');

        setSuccess(true);
        setTimeout(() => {
            setSuccess(false);
            setFormData({ name: '', phone: '', city: '', address: '' });
            setQty(1);
            closeModal();
        }, 1500);
    };

    if (!product) return null;

    const isRTL = i18n.dir() === 'rtl';

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={closeModal} dir={isRTL ? 'rtl' : 'ltr'}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black bg-opacity-25 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-right align-middle shadow-xl transition-all">
                                {success ? (
                                    <div className="text-center py-12 px-6">
                                        <div className="relative mx-auto w-24 h-24 mb-8">
                                            {/* Outer Ring Animation */}
                                            <motion.div
                                                className="absolute inset-0 border-4 border-primary/20 rounded-full"
                                                initial={{ scale: 0.8, opacity: 0 }}
                                                animate={{ scale: 1.2, opacity: 0 }}
                                                transition={{ duration: 1, repeat: Infinity }}
                                            />
                                            {/* Success Icon Animation */}
                                            <motion.div
                                                className="relative flex items-center justify-center w-full h-full bg-primary/10 rounded-full"
                                                initial={{ scale: 0, rotate: -45 }}
                                                animate={{ scale: 1, rotate: 0 }}
                                                transition={{ type: "spring", stiffness: 200, damping: 15 }}
                                            >
                                                <svg className="h-12 w-12 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <motion.path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth="3"
                                                        d="M5 13l4 4L19 7"
                                                        initial={{ pathLength: 0 }}
                                                        animate={{ pathLength: 1 }}
                                                        transition={{ duration: 0.5, delay: 0.2 }}
                                                    />
                                                </svg>
                                            </motion.div>
                                        </div>

                                        <motion.div
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.4 }}
                                        >
                                            <h3 className="text-xl font-bold text-gray-900 mb-2">
                                                {formData.name
                                                    ? t('quickOrderForm.processingName', { name: formData.name })
                                                    : t('quickOrderForm.processingGeneral')}
                                            </h3>
                                            <div className="flex justify-center gap-1 mt-4">
                                                {[0, 1, 2].map((i) => (
                                                    <motion.div
                                                        key={i}
                                                        className="w-2 h-2 bg-primary rounded-full"
                                                        animate={{ scale: [1, 1.5, 1], opacity: [0.3, 1, 0.3] }}
                                                        transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.2 }}
                                                    />
                                                ))}
                                            </div>
                                        </motion.div>
                                    </div>
                                ) : (
                                    <>
                                        <Dialog.Title as="h3" className="text-lg font-bold leading-6 text-gray-900 mb-4 flex justify-between items-center">
                                            <span>{t('common.quickOrder')}</span>
                                            <button onClick={closeModal} className="text-gray-400 hover:text-gray-500">
                                                <span className="sr-only">Close</span>
                                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        </Dialog.Title>

                                        <div className="mb-6 flex items-center gap-4 bg-neutral-50 p-3 rounded-lg">
                                            <img src={getImageUrl(product.images[0])} alt={product.title_ar} className="w-16 h-16 object-cover rounded-md" />
                                            <div className="text-start flex-1">
                                                <h4 className="font-semibold text-sm line-clamp-1">
                                                    {isRTL ? product.title_ar : product.title_fr}
                                                </h4>
                                                <p className="text-primary font-bold text-sm">
                                                    {activeVariant ? activeVariant.price : (product.discount_price || product.price)} {product.currency}
                                                </p>
                                            </div>
                                        </div>

                                        {product.variants && product.variants.length > 0 && (
                                            <div className="mb-6">
                                                <label className="block text-sm font-medium text-gray-700 mb-2 text-start">{t('product.selectOption', 'Select Option')}</label>
                                                <div className="flex flex-wrap gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => setSelectedVariant(null)}
                                                        className={`px-3 py-1.5 rounded-lg border-2 text-sm font-medium transition-all
                                                            ${activeVariant === null
                                                                ? 'border-primary bg-primary text-white'
                                                                : 'border-gray-200 bg-white text-gray-700 hover:border-primary/50'
                                                            }`}
                                                    >
                                                        {/* {t('product.standard', 'Standard')} */}
                                                        {isRTL ? (product.unit_name_ar || product.unit_name_fr) : product.unit_name_fr}

                                                    </button>
                                                    {product.variants.map((v) => (
                                                        <button
                                                            key={v.id}
                                                            type="button"
                                                            onClick={() => setSelectedVariant(v)}
                                                            className={`px-3 py-1.5 rounded-lg border-2 text-sm font-medium transition-all
                                                                ${activeVariant?.id === v.id
                                                                    ? 'border-primary bg-primary text-white'
                                                                    : 'border-gray-200 bg-white text-gray-700 hover:border-primary/50'
                                                                }`}
                                                        >
                                                            {isRTL ? (v.name_ar || v.name) : v.name}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        <div className="mb-6">
                                            <div className="flex items-center justify-between mb-2">
                                                <label className="block text-sm font-medium text-gray-700 text-start">{t('product.quantity', 'Quantity')}</label>
                                                <span className="text-sm font-bold text-primary">
                                                    {t('common.total')}: {totalPrice.toFixed(2)} {product.currency}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-4">
                                                <div className="flex items-center border-2 border-neutral-200 rounded-xl bg-white overflow-hidden shadow-sm">
                                                    <button
                                                        type="button"
                                                        onClick={() => setQty(q => Math.max(1, q - 1))}
                                                        className="w-10 h-10 flex items-center justify-center text-xl font-bold text-gray-600 hover:bg-neutral-50 transition-colors"
                                                    >
                                                        -
                                                    </button>
                                                    <div className="w-10 h-10 flex items-center justify-center font-bold text-lg border-x border-neutral-100">
                                                        {qty}
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => setQty(q => q + 1)}
                                                        className="w-10 h-10 flex items-center justify-center text-xl font-bold text-gray-600 hover:bg-neutral-50 transition-colors"
                                                    >
                                                        +
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        <form onSubmit={handleSubmit} className="space-y-4">
                                            <div>
                                                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1 text-start">
                                                    {t('quickOrderForm.fullName')}
                                                </label>
                                                <input
                                                    type="text"
                                                    name="name"
                                                    id="name"
                                                    required
                                                    value={formData.name}
                                                    onChange={handleChange}
                                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                                                    placeholder={t('quickOrderForm.enterName')}
                                                />
                                            </div>

                                            <div>
                                                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1 text-start">
                                                    {t('quickOrderForm.phone')}
                                                </label>
                                                <input
                                                    type="tel"
                                                    name="phone"
                                                    id="phone"
                                                    required
                                                    value={formData.phone}
                                                    onChange={handleChange}
                                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                                                    placeholder={t('quickOrderForm.phonePlaceholder')}
                                                    dir="ltr"
                                                />
                                            </div>

                                            <div>
                                                <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1 text-start">
                                                    {t('quickOrderForm.city')}
                                                </label>
                                                <input
                                                    type="text"
                                                    name="city"
                                                    id="city"
                                                    required
                                                    value={formData.city}
                                                    onChange={handleChange}
                                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                                                    placeholder={t('quickOrderForm.cityPlaceholder')}
                                                />
                                            </div>

                                            <div>
                                                <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1 text-start">
                                                    {t('quickOrderForm.address')}
                                                </label>
                                                <input
                                                    type="text"
                                                    name="address"
                                                    id="address"
                                                    required
                                                    value={formData.address}
                                                    onChange={handleChange}
                                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                                                    placeholder={t('quickOrderForm.addressPlaceholder')}
                                                />
                                            </div>

                                            {error && (
                                                <p className="text-red-500 text-sm text-start">{error}</p>
                                            )}

                                            <div className="mt-6">
                                                <button
                                                    type="submit"
                                                    disabled={loading}
                                                    className="w-full inline-flex justify-center rounded-full border border-transparent bg-primary px-4 py-3 text-sm font-medium text-white hover:bg-primary-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                                >
                                                    {loading ? t('common.loading') : t('common.quickOrder')}
                                                </button>
                                            </div>
                                        </form>
                                    </>
                                )}
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export default QuickOrderModal;
